import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './edit-preset-modal.module.css';
import { emptyObject } from 'ui-components/default-props/empty';
import { ShippingPresetPropType } from '../../../prop-types/shipping-preset';
import { EditPreset } from '../../edit-preset';
import { presetSchema } from '../../edit-preset/preset-schema';
import { ModalPortal } from '../../modal-portal/index';

const messages = defineMessages({
  editPresetModalTitle: {
    description: 'editPresetModal.title',
    defaultMessage: 'Edit Preset',
    id: 'i7xpvO',
  },
  newPresetModalTitle: {
    description: 'newPresetModal.title',
    defaultMessage: 'New Preset',
    id: 'hS75/R',
  },
  newPresetModalCancel: {
    description: 'newPresetModal.cancel',
    defaultMessage: 'Cancel',
    id: 'xPDlF8',
  },
  newPresetModalSaveChanges: {
    description: 'newPresetModal.saveChanges',
    defaultMessage: 'Save Changes',
    id: 'YJkLZ6',
  },
  newPresetModalSavePreset: {
    description: 'newPresetModal.savePreset',
    defaultMessage: 'Save Preset',
    id: '8yvr+q',
  },
});

const reduceErrorToValidationState = (validation, { path, message }) => ({
  ...validation,
  [path]: message,
});

export const EditPresetModal = ({ preset = emptyObject, isEdit, savePreset, onClose, updatePreset }) => {
  const [validation, setValidation] = useAsyncSafeState({});
  const validateAndSavePreset = useCallback(() => {
    presetSchema
      .validate(preset, { abortEarly: false })
      .then(() => savePreset(preset?.presetId, preset))
      .catch((validationError) => setValidation(validationError.inner.reduce(reduceErrorToValidationState, {})));
  }, [savePreset, preset, setValidation]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style['edit-preset-modal']}>
        <ModalHeader closeModal={onClose}>
          <FormattedMessage {...(isEdit ? messages.editPresetModalTitle : messages.newPresetModalTitle)} />
        </ModalHeader>
        <ModalBody>
          <EditPreset isEdit={isEdit} preset={preset} updatePreset={updatePreset} validation={validation} />
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} theme='tertiary'>
            <FormattedMessage {...messages.newPresetModalCancel} />
          </Button>
          <Button onClick={validateAndSavePreset} theme='primary'>
            <FormattedMessage {...(isEdit ? messages.newPresetModalSaveChanges : messages.newPresetModalSavePreset)} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

EditPresetModal.propTypes = {
  isEdit: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  preset: ShippingPresetPropType,
  savePreset: PropTypes.func.isRequired,
  updatePreset: PropTypes.func.isRequired,
};
